import { Input } from "antd";
import { Trans } from "react-i18next";
import { t } from "i18next";
import { twMerge } from "tailwind-merge";
import { IconLineClose } from "@kalodata/kds-design-icons";
import { ClipUpload } from "@components/ClipUpload/ClipUpload";
import { SafeImage } from "@components/SafeImage";
import { SectionLabel } from "@components/SectionLabel";
import { UserProductCategorySource } from "@apis/generated/openapi";
import { CaptionDisplay, ImageCaptionPopover } from "./ImageCaptionPopover";
import { SellingPointsSection } from "./SellingPointsSection";
import {
  MAX_PRODUCT_IMAGES,
  PRODUCT_TITLE_MAX_LENGTH,
  type ProductFormState,
  type ProductImageFile,
  type ProductImageResponse,
  type SellingPointItem,
} from "./types";
import type { SellingPointsStatus } from "./useProductForm";

const IMAGE_ACCEPT = ["image/png", "image/jpeg", "image/jpg", "image/webp", "image/gif"];

const getMeta = (file: ProductImageFile): ProductImageResponse | undefined =>
  file.response as ProductImageResponse | undefined;

export interface ProductEditorBodyProps {
  form: ProductFormState;
  imageFiles: ProductImageFile[];
  sellingPointsStatus: SellingPointsStatus;
  /** 是否展示 Selling Points Refresh 按钮（仅手动模式） */
  showRefreshSellingPoints?: boolean;
  /** 仅在 sellingPoints 非空 / loading 时才默认展示 SellingPoints；某些场景需要强制展示 */
  alwaysShowSellingPoints?: boolean;
  /** 字段尺寸：modal 内 = `md`（输入框 40 高、label 14）；Studio 内联 = `inline`（输入框 32 高、label 16） */
  variant?: "modal" | "inline";
  /** 图片卡尺寸；默认 158x158（modal）。VideoRemake 内联保持 142x142 */
  imageCardSize?: { width: number; height: number };
  /** 图片上传区下方是否额外加间距 */
  imageBlockClassName?: string;
  onChangeForm: (patch: Partial<ProductFormState>) => void;
  onFilesChange: (files: ProductImageFile[]) => void;
  uploadFile: (file: File, ctx: any) => Promise<any>;
  onChangeCaption: (uid: string, caption: string) => void;
  onChangeSellingPoints: (items: SellingPointItem[]) => void;
  onAddSellingPoint: (content: string) => void;
  onRemoveSellingPoint: (id: string) => void;
  onRefreshSellingPoints?: () => void;
}

/**
 * Create Product Entity 的「核心表单」主体——不包含 Banner、Footer，只渲染图片网格 +
 * Product Name + Category + Selling Points。Modal 入口和 Studio 内联入口共用。
 */
export const ProductEditorBody = ({
  form,
  imageFiles,
  sellingPointsStatus,
  showRefreshSellingPoints,
  alwaysShowSellingPoints,
  variant = "modal",
  imageCardSize,
  imageBlockClassName,
  onChangeForm,
  onFilesChange,
  uploadFile,
  onChangeCaption,
  onChangeSellingPoints,
  onAddSellingPoint,
  onRemoveSellingPoint,
  onRefreshSellingPoints,
}: ProductEditorBodyProps) => {
  const hasMainImage = imageFiles.length > 0 && !!imageFiles[0]?.url;
  const isInline = variant === "inline";
  const cardWidth = imageCardSize?.width ?? 158;
  const cardHeight = imageCardSize?.height ?? 190;
  const labelSize: "sm" | "md" = isInline ? "md" : "sm";
  const inputClass = isInline
    ? "h-[32px] text-[14px] border-kds-border-solid bg-kds-bg-element shadow-[0px_1px_1.5px_0px_var(--kds-shadow)]"
    : "h-[40px] text-[14px]";
  const canRefreshSellingPoints =
    !!showRefreshSellingPoints && hasMainImage && !!form.title.trim() && !!form.category.trim();
  const showSellingPoints =
    alwaysShowSellingPoints ||
    showRefreshSellingPoints ||
    form.sellingPoints.length > 0 ||
    sellingPointsStatus === "loading";

  return (
    <div className="flex flex-col">
      <div className={twMerge("mb-[16px]", imageBlockClassName)}>
        <ClipUpload
          files={imageFiles}
          onFilesChange={onFilesChange}
          uploadFile={uploadFile}
          maxCount={MAX_PRODUCT_IMAGES}
          accept={IMAGE_ACCEPT}
          compressImage
          cardWidth={cardWidth}
          cardHeight={cardHeight}
          gap={12}
          uploadPlaceholder={
            <div className="flex h-full w-full cursor-pointer flex-col items-center justify-center gap-[8px] rounded-[8px] border border-kds-border bg-kds-bg-card transition-colors hover:border-kds-accent">
              <div className="flex size-[32px] items-center justify-center rounded-full bg-kds-bg-subtle text-kds-fg">
                <span className="text-[20px] leading-none">+</span>
              </div>
              <span className="text-[14px] leading-[1.4] text-kds-fg">
                <Trans>Drag or paste</Trans>
              </span>
            </div>
          }
          renderUploadedFile={(file, { remove, preview }) => {
            const meta = getMeta(file);
            const caption = meta?.description ?? "";
            return (
              <div
                data-image-caption-anchor
                className="relative flex flex-col gap-[4px] border border-kds-border px-[8px] py-[4px] pt-[8px] rounded-[8px]"
                style={{ width: cardWidth, height: cardHeight }}
              >
                <div className="group relative overflow-hidden rounded-[8px] border border-kds-border bg-kds-bg-card w-[142px] h-[142px]">
                  <SafeImage
                    src={file.url}
                    alt={caption || file.name}
                    fit="contain"
                    className="h-full w-full cursor-pointer"
                    onClick={preview}
                  />
                  <button
                    type="button"
                    aria-label={t("Remove")}
                    className="absolute top-[6px] right-[6px] hidden size-[20px] cursor-pointer items-center justify-center rounded-full border-none bg-kds-mask text-white group-hover:flex"
                    onClick={(e) => {
                      e.stopPropagation();
                      remove();
                    }}
                  >
                    <IconLineClose size={12} />
                  </button>
                </div>
                <ImageCaptionPopover value={caption} onChange={(c) => onChangeCaption(file.uid, c)}>
                  <CaptionDisplay caption={caption} />
                </ImageCaptionPopover>
              </div>
            );
          }}
        />
      </div>

      <div className="mb-[16px] flex flex-col gap-[6px]">
        <SectionLabel
          required
          tooltip={t("The official product title. We use this in scripts and as a fallback alt text.")}
          size={labelSize}
        >
          <Trans>Product Name</Trans>
        </SectionLabel>
        <Input
          value={form.title}
          maxLength={PRODUCT_TITLE_MAX_LENGTH}
          onChange={(e) => onChangeForm({ title: e.target.value })}
          placeholder={t("e.g., Summer Dress Collection")}
          className={inputClass}
        />
      </div>

      <div className="mb-[16px] flex flex-col gap-[6px]">
        <SectionLabel
          required
          tooltip={t("Helps AI choose the right tone and references for the product.")}
          size={labelSize}
        >
          <Trans>Category</Trans>
        </SectionLabel>
        <Input
          value={form.category}
          onChange={(e) =>
            onChangeForm({
              category: e.target.value,
              categorySource: UserProductCategorySource.USER,
            })
          }
          placeholder={t("e.g., Beauty & Personal Care")}
          className={inputClass}
        />
      </div>

      {showSellingPoints && (
        <div className="mb-[16px]">
          <SellingPointsSection
            items={form.sellingPoints}
            status={sellingPointsStatus}
            onChange={onChangeSellingPoints}
            onAdd={onAddSellingPoint}
            onRemove={onRemoveSellingPoint}
            onRefresh={canRefreshSellingPoints ? onRefreshSellingPoints : undefined}
          />
        </div>
      )}
    </div>
  );
};
